import { z } from "zod";

/* ===========================================================================
 * US-E8.F1-0052 View Fund Dashboard + US-E8.F1-0053 Empty State (@UC0046/@UC0063)
 * GET /v1/funds/:id/dashboard — Phase 9 Transparency/Reports (read-only).
 *
 * Balance O(1) từ account_balances (KHÔNG SUM). Redaction theo transparency_mode:
 *  - public → Member thấy ĐỦ (balance + thu/chi + 5 tx + chart) như Admin.
 *  - private (default, FAIL-CLOSED) → Member KHÔNG thấy balance + số tài chính (chỉ metadata).
 * Empty-state (0053): transactions.count=0 → isEmpty=true; client render CTA theo canManage.
 * Tiền STRING minor unit (B1, no float).
 * ========================================================================= */

const MONEY_INTEGER_STRING_PATTERN = /^-?\d+$/;

/** Key config minh bạch (SystemSetting/ConfigOverride) — US-E8.F1-0052. */
export const TRANSPARENCY_MODE_KEY = "transparency_mode";

export const TRANSPARENCY_MODE = {
  PUBLIC: "public",
  PRIVATE: "private",
} as const;

export type TransparencyMode =
  (typeof TRANSPARENCY_MODE)[keyof typeof TRANSPARENCY_MODE];

export const transparencyModeSchema = z.enum([
  TRANSPARENCY_MODE.PUBLIC,
  TRANSPARENCY_MODE.PRIVATE,
]);

/** 1 giao dịch gần nhất (đọc từ ledger_transactions, KHÔNG format tiền server). */
export const dashboardTransactionSchema = z
  .object({
    id: z.string().uuid(),
    txType: z.string(),
    /** amount tổng của tx = Σ debit (STRING minor unit, B1). */
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    memo: z.string().nullable(),
    createdAt: z.string().datetime(),
  })
  .strict();

export type DashboardTransaction = z.infer<typeof dashboardTransactionSchema>;

/** 1 điểm chart theo tháng (YYYY-MM) — income/expense minor unit STRING. */
export const dashboardChartPointSchema = z
  .object({
    month: z.string().regex(/^\d{4}-\d{2}$/),
    incomeMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    expenseMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
  })
  .strict();

export type DashboardChartPoint = z.infer<typeof dashboardChartPointSchema>;

/**
 * Response dashboard. `balance`/`periodIncome`/`periodExpense`/`monthlyChart` OMIT khi Member +
 * transparency=private (fail-closed redaction — KHÔNG trả về client). `canManage` = Admin shortcut/CTA.
 * `isEmpty` = transactions.count===0 (0053 empty-state; client render CTA theo canManage).
 */
export const fundDashboardResponseSchema = z
  .object({
    fundId: z.string().uuid(),
    fundName: z.string(),
    currency: z.string().length(3),
    /** Số dư hiện tại (CASH) — OMIT khi redacted. */
    balance: z.string().regex(MONEY_INTEGER_STRING_PATTERN).optional(),
    /** Tổng thu kỳ này (tháng hiện tại) — OMIT khi redacted. */
    periodIncome: z.string().regex(MONEY_INTEGER_STRING_PATTERN).optional(),
    /** Tổng chi kỳ này (tháng hiện tại) — OMIT khi redacted. */
    periodExpense: z.string().regex(MONEY_INTEGER_STRING_PATTERN).optional(),
    /** Chuỗi theo tháng — OMIT khi redacted. */
    monthlyChart: z.array(dashboardChartPointSchema).optional(),
    /** 5 giao dịch gần nhất — OMIT khi redacted. */
    recentTransactions: z.array(dashboardTransactionSchema).optional(),
    /** true = quỹ chưa có giao dịch (0053 empty-state). */
    isEmpty: z.boolean(),
    /** true = Admin/Treasurer (render shortcut "Tạo đợt thu"/"Ghi chi tiêu" / CTA empty). */
    canManage: z.boolean(),
    /** true = số tài chính bị ẩn cho Member (transparency=private) → client hiện thông báo. */
    redacted: z.boolean(),
  })
  .strict();

export type FundDashboardResponse = z.infer<typeof fundDashboardResponseSchema>;
